import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, ChevronLeft, ChevronRight, Loader2, Send, X, CalendarCheck, Info, LogIn, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { getSamplePreviewEnabled, subscribeToSamplePreviewMode } from '../lib/samplePreviewMode';
import { trackEvent } from '../lib/analytics';
import {
  formatDate,
  formatMonthYear,
  getWeekdayLabels,
  translateTemplate,
} from '../lib/publicListingTruth';
import {
  createClearedDateSelection,
  getSamplePreviewCalendarData,
  isCalendarDateUnavailable,
  normalizeBlockedDateSet,
  resolveCheckoutSelection,
} from '../lib/bookingCalendarRange';
import { buildBookingsReturnUrl } from '../lib/bookingInquiryContext';
import {
  buildInquiryCreatePayload,
  formatInquirySuccessMessage,
  getCreatedInquiryId,
  isSuccessfulInquiryCreateResponse,
} from '../lib/bookingInquirySubmit';
import { isEmailVerificationRequiredError } from '../lib/emailVerificationFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hasValue = (value) => value !== null && value !== undefined && value !== '';
const formatCurrency = (value) => (hasValue(value) ? `€${Number(value).toLocaleString()}` : '—');

function InquirySignInPanel({ listingId, t }) {
  const redirect = encodeURIComponent(`/listings/${listingId}`);
  return (
    <div
      className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
      data-testid="inquiry-sign-in-panel"
    >
      <p className="text-sm font-semibold text-slate-800">
        {t('booking.inquiry_sign_in_title', 'Sign in to send an inquiry')}
      </p>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
        {t(
          'booking.inquiry_sign_in_body',
          'Create an account or sign in so owners know who they are speaking with.',
        )}
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <Link
          to={`/login?redirect=${redirect}`}
          className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 bg-signal-blue hover:bg-signal-blue-hover text-white text-sm font-medium rounded-md transition-colors"
        >
          <LogIn className="w-4 h-4" />
          {t('auth.login', 'Sign In')}
        </Link>
        <Link
          to={`/register?redirect=${redirect}`}
          className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-md transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          {t('auth.register', 'Create Account')}
        </Link>
      </div>
    </div>
  );
}

function InquiryOwnerNoticePanel({ t }) {
  return (
    <div
      className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
      data-testid="inquiry-owner-notice-panel"
    >
      <p className="text-sm font-semibold text-slate-800">
        {t('booking.inquiry_owner_title', 'You own this listing')}
      </p>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
        {t(
          'booking.inquiry_owner_body',
          'Guests can send inquiries from this panel. Owners cannot inquire on their own listings.',
        )}
      </p>
    </div>
  );
}

export default function BookingCalendar({ listingId, listingTitle, listingOwnerId, monthlyAmount }) {
  const { user, isAuthenticated } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState({ checkIn: null, checkOut: null });
  const [selecting, setSelecting] = useState('checkIn');
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [samplePreviewEnabled, setSamplePreviewState] = useState(getSamplePreviewEnabled());
  const [inquiryForm, setInquiryForm] = useState({ guests: 1, message: '', special_requests: '' });
  const [rangeErrorKey, setRangeErrorKey] = useState('');

  const blockedDateSet = useMemo(
    () => normalizeBlockedDateSet(calendarData?.blocked_dates || []),
    [calendarData?.blocked_dates],
  );

  const isListingOwner = Boolean(
    isAuthenticated && listingOwnerId && user?.id === listingOwnerId,
  );
  const canSubmitInquiry = isAuthenticated && !isListingOwner;

  useEffect(() => subscribeToSamplePreviewMode(setSamplePreviewState), []);
  useEffect(() => { loadCalendar(); }, [listingId, currentMonth, samplePreviewEnabled]);

  useEffect(() => {
    if (!canSubmitInquiry) {
      setShowInquiryForm(false);
    }
  }, [canSubmitInquiry]);

  const loadCalendar = async () => {
    setLoading(true);
    if (samplePreviewEnabled) {
      setCalendarData(getSamplePreviewCalendarData());
      setLoading(false);
      return;
    }
    try {
      const today = new Date();
      const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthsAhead = Math.max(3, (currentMonthStart.getFullYear() - today.getFullYear()) * 12 + (currentMonthStart.getMonth() - today.getMonth()) + 2);
      const res = await fetch(`${API_URL}/api/bookings/listings/${listingId}/calendar?months=${monthsAhead}`);
      const data = await res.json();
      setCalendarData(data);
    } catch (error) {
      console.error('Failed to load calendar:', error);
      if (samplePreviewEnabled) setCalendarData(getSamplePreviewCalendarData());
      else toast.error(t('booking.toast.calendar_load_failed', 'Failed to load availability calendar'));
    } finally {
      setLoading(false);
    }
  };

  const isDateUnavailable = (date) => isCalendarDateUnavailable(date, blockedDateSet);

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleClearDates = () => {
    const cleared = createClearedDateSelection();
    setSelectedDates(cleared.selectedDates);
    setSelecting(cleared.selecting);
    setRangeErrorKey(cleared.rangeErrorKey);
    setShowInquiryForm(false);
  };

  const handleDateClick = (date) => {
    if (isDateUnavailable(date) || isDateInPast(date)) return;

    if (selecting === 'checkIn') {
      setSelectedDates({ checkIn: date, checkOut: null });
      setSelecting('checkOut');
      setRangeErrorKey('');
      return;
    }

    const resolution = resolveCheckoutSelection({
      checkIn: selectedDates.checkIn,
      checkOut: date,
      blockedDateSet,
    });

    if (!resolution.accepted) {
      setSelectedDates(resolution.selectedDates);
      setSelecting(resolution.selecting);
      if (resolution.errorKey === 'unavailable') {
        setRangeErrorKey('booking.range_includes_unavailable');
      } else {
        setRangeErrorKey('');
      }
      return;
    }

    setSelectedDates(resolution.selectedDates);
    setSelecting(resolution.selecting);
    setRangeErrorKey('');
  };

  const isDateSelected = (date) => {
    if (!selectedDates.checkIn) return false;
    if (selectedDates.checkOut) return date >= selectedDates.checkIn && date <= selectedDates.checkOut;
    return date.getTime() === selectedDates.checkIn.getTime();
  };
  const isCheckInDate = (date) => selectedDates.checkIn && date.getTime() === selectedDates.checkIn.getTime();
  const isCheckOutDate = (date) => selectedDates.checkOut && date.getTime() === selectedDates.checkOut.getTime();
  const calculateNights = () => (!selectedDates.checkIn || !selectedDates.checkOut ? 0 : Math.ceil((selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)));

  const handleSubmitInquiry = async () => {
    if (!canSubmitInquiry) return;
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      toast.error(t('booking.no_dates_selected', 'Please select check-in and check-out dates'));
      return;
    }
    setSubmitting(true);
    try {
      if (samplePreviewEnabled) {
        toast.success(
          t(
            'booking.sample_preview_submit_success',
            'Sample preview mode: booking inquiry demo completed. No record was saved.',
          ),
        );
        trackEvent('inquiry_submitted', { source: 'sample_preview' });
        setShowInquiryForm(false);
        setSelectedDates({ checkIn: null, checkOut: null });
        setInquiryForm({ guests: 1, message: '', special_requests: '' });
        loadCalendar();
        return;
      }

      const payload = buildInquiryCreatePayload({
        listingId,
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut,
        guests: inquiryForm.guests,
        message: inquiryForm.message,
        special_requests: inquiryForm.special_requests,
      });
      const res = await api.post('/bookings/inquiry', payload);
      if (!isSuccessfulInquiryCreateResponse(res.data)) {
        throw new Error(t('booking.submit_failed', 'Failed to submit inquiry'));
      }

      const inquiryId = getCreatedInquiryId(res.data);
      toast.success(formatInquirySuccessMessage(t, res.data));
      trackEvent('inquiry_submitted', { source: 'live', inquiry_id: inquiryId });
      setShowInquiryForm(false);
      setSelectedDates({ checkIn: null, checkOut: null });
      setInquiryForm({ guests: 1, message: '', special_requests: '' });
      loadCalendar();
      if (inquiryId) {
        navigate(buildBookingsReturnUrl(inquiryId));
      }
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(
          t('verify.required_action', 'Please verify your email before continuing.'),
        );
        navigate('/verify-email-pending');
        return;
      }
      const message = error.response?.data?.detail || t('booking.submit_failed', 'Failed to submit inquiry');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderCalendarMonth = (monthOffset = 0) => {
    const month = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const firstDayOfWeek = month.getDay();
    const weekdayLabels = getWeekdayLabels(locale);
    const days = [];
    for (let index = 0; index < firstDayOfWeek; index += 1) {
      days.push(<div key={`empty-${index}`} className="h-8" />);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const unavailable = isDateUnavailable(date);
      const inPast = isDateInPast(date);
      const selected = isDateSelected(date);
      const isCheckIn = isCheckInDate(date);
      const isCheckOut = isCheckOutDate(date);
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          disabled={unavailable || inPast}
          className={`h-8 w-8 rounded-full text-sm transition-colors ${unavailable ? 'bg-red-50 text-red-500 line-through cursor-not-allowed' : inPast ? 'text-slate-300 cursor-not-allowed' : selected ? isCheckIn || isCheckOut ? 'bg-signal-blue text-white font-bold' : 'bg-signal-blue/15 text-signal-blue' : 'text-slate-700 hover:bg-signal-blue/10 hover:text-signal-blue'}`}
        >
          {day}
        </button>
      );
    }
    return (
      <div className="p-3">
        <div className="text-center text-sm font-semibold text-slate-800 mb-2">{formatMonthYear(month, locale)}</div>
        <div className="grid grid-cols-7 gap-1 text-xs text-slate-400 mb-1">{weekdayLabels.map((dayLabel, index) => <div key={`weekday-${index}`} className="h-6 flex items-center justify-center font-medium">{dayLabel}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  if (loading) {
    return <div className="glass-card p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 text-signal-blue animate-spin" /></div>;
  }

  const nights = calculateNights();

  return (
    <div className="glass-card p-4" data-testid="booking-calendar">
      <div className="flex items-center gap-2 mb-4"><Calendar className="w-5 h-5 text-signal-blue" /><h3 className="font-heading text-lg font-bold text-slate-800">{t('booking.check_availability', 'Check Availability')}</h3></div>
      {samplePreviewEnabled && (
        <div className="mb-4 p-3 bg-signal-blue/10 border border-signal-blue/20 rounded-lg text-xs text-signal-blue">
          {t(
            'booking.sample_preview_banner',
            'Sample preview mode is active. Calendar availability is demo-only with no real blocks; inquiry submission is a walkthrough only.',
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm text-slate-600">
          {selecting === 'checkIn'
            ? t('booking.select_check_in', 'Select check-in date')
            : t('booking.select_check_out', 'Select check-out date')}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="text-slate-500 hover:text-slate-800"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-slate-50 rounded-xl border border-slate-200 divide-y md:divide-y-0 md:divide-x divide-slate-200">{renderCalendarMonth(0)}{renderCalendarMonth(1)}</div>
      <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500"><div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-signal-blue" /><span>{t('booking.legend_selected', 'Selected')}</span></div><div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-100 border border-red-300" /><span>{t('booking.legend_unavailable', 'Unavailable')}</span></div><div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-slate-200" /><span>{t('booking.legend_past', 'Past')}</span></div></div>
      {rangeErrorKey && (
        <div
          className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800"
          data-testid="booking-range-error"
        >
          {t(
            rangeErrorKey,
            'This date range includes unavailable dates. Choose a different check-out date or clear dates.',
          )}
        </div>
      )}
      {selectedDates.checkIn && (
        <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-start justify-between gap-2 text-sm">
            <div className="min-w-0">
              <div>
                <span className="text-slate-500">{t('booking.check_in', 'Check-in')}:</span>
                <span className="text-slate-800 ml-2 font-medium">{formatDate(selectedDates.checkIn, '—', locale)}</span>
              </div>
              {selectedDates.checkOut && (
                <div className="mt-1">
                  <span className="text-slate-500">{t('booking.check_out', 'Check-out')}:</span>
                  <span className="text-slate-800 ml-2 font-medium">{formatDate(selectedDates.checkOut, '—', locale)}</span>
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs shrink-0"
              onClick={handleClearDates}
              data-testid="booking-clear-dates-btn"
            >
              {t('booking.clear_dates', 'Clear dates')}
            </Button>
          </div>
          {nights > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between">
              <div className="text-slate-600 text-sm">
                {nights === 1
                  ? `1 ${t('booking.night_singular', 'night')}`
                  : translateTemplate(t('booking.nights_count', '{count} nights'), { count: nights })}
              </div>
              {hasValue(monthlyAmount) && (
                <div className="text-right">
                  <span className="text-xs text-slate-500">{t('booking.reference_monthly_price', 'Reference monthly price:')} </span>
                  <span className="text-sm font-semibold text-military-gold">{formatCurrency(monthlyAmount)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-slate-600 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 text-blue-400 flex-shrink-0" />
        <p>
          {t(
            'booking.calendar_disclaimer',
            'Calendar selection helps you request dates. Final pricing, deposit timing, and payment steps are confirmed during inquiry and in the rental agreement — not estimated here in the browser.',
          )}
        </p>
      </div>

      {!isAuthenticated && <InquirySignInPanel listingId={listingId} t={t} />}
      {isListingOwner && <InquiryOwnerNoticePanel t={t} />}
      {canSubmitInquiry && selectedDates.checkIn && selectedDates.checkOut && (
        <Button
          onClick={() => {
            trackEvent('inquiry_started', { source: 'booking_calendar' });
            setShowInquiryForm(true);
          }}
          className="w-full mt-4 bg-signal-blue hover:bg-signal-blue-hover text-white"
          data-testid="request-booking-btn"
        >
          <CalendarCheck className="w-4 h-4 mr-2" />
          {t('booking.request_dates', 'Request These Dates')}
        </Button>
      )}

      {showInquiryForm && canSubmitInquiry && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-slate-800">{t('booking.inquiry_title', 'Booking Inquiry')}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowInquiryForm(false)} className="text-slate-600">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="text-sm text-slate-600 mb-1">{listingTitle}</div>
                <div className="flex items-center justify-between text-slate-200">
                  <span>
                    {formatDate(selectedDates.checkIn, '—', locale)} - {formatDate(selectedDates.checkOut, '—', locale)}
                  </span>
                  <Badge className="bg-signal-blue/20 text-signal-blue border-signal-blue/30">
                    {translateTemplate(t('booking.nights_count', '{count} nights'), { count: nights })}
                  </Badge>
                </div>
                {hasValue(monthlyAmount) && (
                  <div className="mt-2 text-right">
                    <span className="text-military-gold font-bold">{formatCurrency(monthlyAmount)}</span>
                    <span className="text-xs text-slate-500 ml-1">{t('booking.monthly_reference', 'monthly reference')}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">{t('booking.guests', 'Number of Guests')}</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={inquiryForm.guests}
                  onChange={(e) => setInquiryForm((prev) => ({ ...prev, guests: parseInt(e.target.value, 10) || 1 }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100 placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">{t('booking.message_to_owner', 'Message to Owner')}</label>
                <textarea
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm((prev) => ({ ...prev, message: e.target.value }))}
                  placeholder={t(
                    'booking.message_placeholder',
                    'Introduce yourself and share details about your PCS...',
                  )}
                  rows={3}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100 placeholder:text-slate-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">{t('booking.special_requests', 'Special Requests (optional)')}</label>
                <textarea
                  value={inquiryForm.special_requests}
                  onChange={(e) => setInquiryForm((prev) => ({ ...prev, special_requests: e.target.value }))}
                  placeholder={t(
                    'booking.special_requests_placeholder',
                    'Pet-friendly, parking needs, early check-in...',
                  )}
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-slate-100 placeholder:text-slate-500 resize-none"
                />
              </div>
              <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-xs text-slate-600">
                <p className="mb-2">
                  {t(
                    'booking.inquiry_ack_intro',
                    'By submitting this inquiry, you acknowledge that:',
                  )}
                </p>
                <ul className="list-disc list-inside space-y-1 text-slate-500">
                  <li>
                    {t(
                      'booking.inquiry_ack_item_request',
                      'This is a request, not a confirmed booking',
                    )}
                  </li>
                  <li>
                    {t(
                      'booking.inquiry_ack_item_pricing',
                      'Final pricing and deposits are confirmed by the owner before contract or payment',
                    )}
                  </li>
                  <li>
                    {t(
                      'booking.inquiry_ack_item_contract',
                      'The rental contract is between you and the property owner',
                    )}
                  </li>
                </ul>
              </div>
              <Button
                onClick={handleSubmitInquiry}
                disabled={submitting}
                className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white"
                data-testid="submit-inquiry-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.submitting', 'Submitting...')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('booking.submit', 'Send Inquiry')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
